// Libs
import classNames from 'classnames/bind';
import {
  Heading,
  IconButton,
  IconCheckLine,
  IconEditLine,
  Spinner,
  Table,
  Text,
  TextInput,
  View,
} from '@instructure/ui';
import { useTranslation } from 'react-i18next';
import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
// Components, Layouts, Pages
import { BasePagination } from '~/components/common';
// Others
import { useAppDispatch, useAppSelector } from '~/utils/redux/hooks';
import { getCourses, updateCourses } from '~/thunks/course/courseThunk';
import { DEFAULT_LIMIT_PAGE, DEFAULT_PAGE } from '~/utils/constants/common';
import { ICourse, IQueryGetCourses } from '~/utils/interface/course';
import { courseAction } from '~/thunks/course/courseSlice';
// Styles, images, icons
import styles from './Course.module.scss';
import { icons } from '~/assets';

type Props = {
  content?: string;
};

const cx = classNames.bind(styles);

const Course = (props: Props) => {
  //#region Destructuring Props
  const {} = props;
  //#endregion Destructuring Props

  //#region Declare Hook
  const { t } = useTranslation(['common/home', 'common/base', 'admin/course']);
  const navigate = useNavigate();
  const dispatch = useAppDispatch();
  //#endregion Declare Hook

  //#region Selector
  const { courses: coursesResponse, isLoading, hasMore, currentPage } = useAppSelector((state) => state.courseState);
  //#endregion Selector

  //#region Declare State
  const [courseFocusId, setCourseFocusId] = useState<String>();
  const [courses, setCourses] = useState<ICourse[]>([]);
  const [page, setPage] = useState<number>(DEFAULT_PAGE);
  //#endregion Declare State

  //#region Declare Memo
  //#endregion Declare Memo

  //#region Implement Hook
  useEffect(() => {
    handleGetCourses();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    setCourses(coursesResponse);
  }, [coursesResponse]);
  //#endregion Implement Hook

  //#region Handle Function
  const handleBack = () => {
    navigate(-1);
  };

  const handleGetCourses = async (query?: IQueryGetCourses) => {
    await dispatch(
      getCourses(
        query ?? {
          limit: DEFAULT_LIMIT_PAGE,
          page: page,
        }
      )
    ).unwrap();
  };

  const handleUpdateCourse = (courseId: string, totalExams: number) => {
    setCourseFocusId(undefined);
    dispatch(
      updateCourses({
        courses: [
          {
            id: courseId.toString(),
            totalExams,
          },
        ],
      })
    )
      .unwrap()
      .then(() => {
        dispatch(courseAction.setNewCourses(courses));
      });
  };

  const handleClickEdit = (courseId: string) => {
    setCourses(coursesResponse);
    setCourseFocusId(courseId);
  };

  const handleChangeValue = (courseId: string, value: string) => {
    if (isNaN(Number(value))) return;

    setCourses((prevState) =>
      prevState.map((item) => {
        if (courseId === item.id) {
          return {
            ...item,
            totalExams: value ? Number(value) : 0,
          };
        }
        return item;
      })
    );
  };

  const handleChangePage = (newPage: number) => {
    setPage(newPage);
    handleGetCourses({
      limit: DEFAULT_LIMIT_PAGE,
      page: newPage,
    });
  };

  const renderContent = () => {
    if (isLoading && coursesResponse.length === 0) {
      return (
        <div className={cx('spinnerContainer')}>
          <Spinner size='small' />
        </div>
      );
    }

    if (coursesResponse.length === 0) {
      return (
        <div className={cx('spinnerContainer')}>
          <Text variant='contentSmall'>{t('common_no_data_available', { ns: 'common/base' })}</Text>
        </div>
      );
    }

    return (
      <View className={cx('courseListContainer')}>
        <Table layout='auto'>
          <Table.Head>
            <Table.Row>
              <Table.ColHeader id='Name'>{t('course_name_title', { ns: 'admin/course' })}</Table.ColHeader>
              <Table.ColHeader id='Student'>{t('course_total_student_title', { ns: 'admin/course' })}</Table.ColHeader>
              <Table.ColHeader id='Exam'>{t('course_total_exam_title', { ns: 'admin/course' })}</Table.ColHeader>
            </Table.Row>
          </Table.Head>
          <Table.Body>
            {courses.map((data, index) => (
              <Table.Row key={index}>
                <Table.Cell>
                  <span>{data.name}</span>
                </Table.Cell>

                <Table.Cell>
                  <span>{data.totalStudents}</span>
                </Table.Cell>

                <Table.Cell>
                  <span>
                    {data.id === courseFocusId ? (
                      <TextInput
                        type={'tel'}
                        width={'120px'}
                        value={data.totalExams.toString()}
                        onChange={(e, newValue) => handleChangeValue(data.id, newValue)}
                        renderAfterInput={
                          <IconButton
                            withBorder={false}
                            withBackground={false}
                            color='primary'
                            onClick={() => handleUpdateCourse(data.id, data.totalExams)}
                            screenReaderLabel={'Save'}
                          >
                            {<IconCheckLine />}
                          </IconButton>
                        }
                      />
                    ) : (
                      <>
                        {data.totalExams}
                        <IconButton
                          withBorder={false}
                          withBackground={false}
                          color='primary'
                          onClick={() => handleClickEdit(data.id)}
                          screenReaderLabel={'Edit'}
                        >
                          {<IconEditLine />}
                        </IconButton>
                      </>
                    )}
                  </span>
                </Table.Cell>
              </Table.Row>
            ))}
          </Table.Body>
        </Table>

        <BasePagination currentPage={page} hasMore={hasMore} onChange={handleChangePage} />
      </View>
    );
  };
  //#endregion Handle Function

  return (
    <div className={cx('viewPort')}>
      <div className={cx('backContainer')} onClick={handleBack}>
        <img src={icons.commonIconBack} alt={t('common_alt_img', { ns: 'common/base' })} className={cx('iconBack')} />
        <Text variant='content'>{t('common_back_button', { ns: 'common/base' })}</Text>
      </div>

      <div className={cx('container')}>
        <View as='div' className={cx('courseTitle')}>
          <Heading level='h3'>{t('course_label', { ns: 'admin/course' })}</Heading>
          <img
            className={cx('iconHeader')}
            src={icons.commonIconCourse}
            alt={t('common_alt_img', { ns: 'common/base' })}
          />
        </View>

        {renderContent()}
      </div>
    </div>
  );
};

export default Course;
